/**
 * Order time display helpers.
 *
 * Saved order times are shown in the VIEWER's own timezone (browser / device),
 * not the stop's — see docs/TIMEZONES.md. Every formatter therefore takes
 * `viewerTz` explicitly; components get it from `useViewerTimezone()`. Nothing
 * in this file reads the browser timezone itself, which keeps it testable.
 *
 * (Order *input* is always Central — that lives in `@/lib/timezone`
 * `formLocalToUtc` / `utcToFormLocal`, not here.)
 */
import { formatInZone } from "@/lib/timezone";

/**
 * Lateness helpers — pure functions used by the Orders table and Dashboard
 * to flag orders that have blown past their ready/delivery windows.
 *
 * "Latest" time = the end of the window when present, else the single
 * scheduled time. Comparisons are timestamp-based so timezone math is
 * implicit.
 */
type LatenessOrder = {
  status?: string | null;
  ready_time?: string | null;
  ready_time_end?: string | null;
  requested_delivery_time?: string | null;
  requested_delivery_time_end?: string | null;
};

export function latestReadyTime(order: LatenessOrder): Date | null {
  const t = order.ready_time_end || order.ready_time;
  return t ? new Date(t) : null;
}

export function latestDeliveryTime(order: LatenessOrder): Date | null {
  const t = order.requested_delivery_time_end || order.requested_delivery_time;
  return t ? new Date(t) : null;
}

export function isLateDeparture(order: LatenessOrder, now: Date = new Date()): boolean {
  if (order.status !== "confirmed") return false;
  const latest = latestReadyTime(order);
  return !!latest && now.getTime() > latest.getTime();
}

export function isLateArrival(order: LatenessOrder, now: Date = new Date()): boolean {
  if (order.status !== "in_transit") return false;
  const latest = latestDeliveryTime(order);
  return !!latest && now.getTime() > latest.getTime();
}

type Utc = string | null | undefined;

/** Viewer's abbreviation for an instant, e.g. "MST". "" for empty input. */
export function viewerTzAbbr(utc: Utc, viewerTz: string): string {
  return formatInZone(utc, viewerTz, "zzz");
}

/** Format one instant in the viewer's timezone. */
export function formatTime(utc: Utc, viewerTz: string, fmt: string): string {
  return formatInZone(utc, viewerTz, fmt);
}

/** Format one instant in the viewer's timezone with the abbreviation appended
 *  (e.g. "Jul 15, 07:00 MST"). Prefer this for anything a person reads. */
export function formatTimeWithTz(utc: Utc, viewerTz: string, fmt: string): string {
  const main = formatInZone(utc, viewerTz, fmt);
  if (!main) return "";
  return `${main} ${viewerTzAbbr(utc, viewerTz)}`;
}

/**
 * "start – end" when the endpoints differ, or just "start" when they collapse.
 */
export function formatWindow(startUtc: Utc, endUtc: Utc, viewerTz: string, fmt: string): string {
  const startStr = formatTime(startUtc, viewerTz, fmt);
  if (!endUtc || !startUtc || endUtc === startUtc) return startStr;
  const endStr = formatTime(endUtc, viewerTz, fmt);
  if (endStr === startStr) return startStr;
  return `${startStr} – ${endStr}`;
}

/** Whole days between two instants' calendar dates in `viewerTz`. */
function dayDelta(startUtc: string, endUtc: string, viewerTz: string): number {
  const key = (u: string) => {
    const k = formatTime(u, viewerTz, "yyyy-MM-dd");
    return Date.UTC(Number(k.slice(0, 4)), Number(k.slice(5, 7)) - 1, Number(k.slice(8, 10)));
  };
  return Math.round((key(endUtc) - key(startUtc)) / 86400000);
}

/**
 * Compact window: the date once + a single time range, in the viewer's tz.
 * - Single instant or start === end → "MMM d, HH:mm [TZ]"
 * - Same day                          → "MMM d, HH:mm – HH:mm [TZ]"
 * - Crosses midnight (1..6 days)      → "MMM d, HH:mm – HH:mm [TZ] (+Nd)"
 * - ≥7 day span (rare)                → "MMM d, HH:mm – MMM d, HH:mm [TZ]"
 * The abbreviation is shown unless `withTz: false`.
 */
export function formatWindowCompact(
  startUtc: Utc,
  endUtc: Utc,
  viewerTz: string,
  dateFmt: string,
  timeFmt: string,
  opts: { withTz?: boolean } = {},
): string {
  if (!startUtc) return "";
  const startDate = formatTime(startUtc, viewerTz, dateFmt);
  const startTime = formatTime(startUtc, viewerTz, timeFmt);
  const abbr = opts.withTz === false ? "" : viewerTzAbbr(startUtc, viewerTz);
  const tzSuffix = abbr ? ` ${abbr}` : "";
  if (!endUtc || endUtc === startUtc) return `${startDate}, ${startTime}${tzSuffix}`;

  const endDate = formatTime(endUtc, viewerTz, dateFmt);
  const endTime = formatTime(endUtc, viewerTz, timeFmt);
  if (endDate === startDate && endTime === startTime) return `${startDate}, ${startTime}${tzSuffix}`;

  const days = dayDelta(startUtc, endUtc, viewerTz);
  if (days <= 0) return `${startDate}, ${startTime} – ${endTime}${tzSuffix}`;
  if (days >= 7) return `${startDate}, ${startTime} – ${endDate}, ${endTime}${tzSuffix}`;
  return `${startDate}, ${startTime} – ${endTime}${tzSuffix} (+${days}d)`;
}

/**
 * Time-range portion only (for sub-lines under a date headline), viewer's tz.
 * - Single instant / equal endpoints → "HH:mm"
 * - Same day                         → "HH:mm – HH:mm"
 * - Crosses midnight (1..6 days)     → "HH:mm – HH:mm (+Nd)"
 * - ≥7 day span                      → "MMM d, HH:mm – MMM d, HH:mm"
 */
export function formatTimeRange(startUtc: Utc, endUtc: Utc, viewerTz: string, timeFmt: string): string {
  if (!startUtc) return "";
  const startTime = formatTime(startUtc, viewerTz, timeFmt);
  if (!endUtc || endUtc === startUtc) return startTime;
  const endTime = formatTime(endUtc, viewerTz, timeFmt);
  const days = dayDelta(startUtc, endUtc, viewerTz);
  if (days === 0) return startTime === endTime ? startTime : `${startTime} – ${endTime}`;
  if (days >= 7 || days < 0) {
    return `${formatTime(startUtc, viewerTz, "MMM d")}, ${startTime} – ${formatTime(endUtc, viewerTz, "MMM d")}, ${endTime}`;
  }
  return `${startTime} – ${endTime} (+${days}d)`;
}
